import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {UserModel} from '../../user/user.model';
import {selectPartnerId} from '../../user/user.reducer';
import {SegmentSearchFilterModel} from './search/search-filter.model';
import {SegmentShareService} from '../../segment-multi-share/segment-share.service';
import {SegmentListTableComponent} from './table/table.component';
import {ExportPreparerService} from './export-preparer.service';
import {Store} from '@ngrx/store';
import {SegmentFacadeService} from '../segment-facade.service';
import {Observable} from 'rxjs';
import {SegmentListConfig} from '../segment-config.model';
import {ApiVersion} from '../../app.types';
import {TreeTableComponent} from './tree-table/tree-table.component';
import {MatDialog} from '@angular/material/dialog';
import {ExportPricesDialogComponent} from './export-prices/export-prices-dialog/export-prices-dialog.component';
import {CustomConfigurationFacadeService} from '../../custom-configuration/custom-configuration-facade.service';
import {
  BulkDeleteSegmentsData,
  BulkDeleteSegmentsDialogComponent
} from './bulk-delete-segments-dialog/bulk-delete-segments-dialog.component';

@Component({
  selector: 'app-segment-list',
  template: `
    <div id="filter-container">
      <div class="table-filter-controls">
        <app-segment-search [config]="config" (searchForm)="handleSearchForm($event)"></app-segment-search>
        <div class="button-container" *ngIf="config.showFilterButtons">
          <ng-container *ngIf="partnerId$ | async as partnerId">
            <button *ngIf="showCreateSegment(partnerId)"
                    (click)="create()"
                    id="btn-add-segment"
                    data-cy="button-create-segment"
                    mat-raised-button
                    color="primary"
                    matTooltip="Create segment"
                    name="create-segment">
              <mat-icon>create</mat-icon>
              Create new segment
            </button>
          </ng-container>
          <button (click)="handleExport()"
                  id="btn-export-segments"
                  mat-raised-button
                  color="primary"
                  matTooltip="Export"
                  name="segment-export">
            <mat-icon>save_alt</mat-icon>
            Export segments ({{ segmentSelection.length >= 1 ? segmentSelection.length : 'All' }})
          </button>
          <button (click)="handleExportPrices()"
                  id="btn-export-segments"
                  mat-raised-button
                  color="primary"
                  matTooltip="Export"
                  name="segment-export">
            <mat-icon>save_alt</mat-icon>
            Export segments prices
          </button>
          <button id="bulk-share" color="primary"
                  [disabled]="segmentSelection.length <= 1"
                  (click)="multiShare()"
                  mat-raised-button>
            <mat-icon>share</mat-icon>
            <span id="bulk-counter">Bulk operation ({{ segmentSelection.length }})</span>
          </button>
          <button id="bulk-delete" color="accent"
                  [disabled]="segmentSelection.length < 1"
                  (click)="bulkDelete()"
                  mat-raised-button>
            <mat-icon>delete</mat-icon>
            <span id="bulk-counter">Delete ({{ segmentSelection.length }})</span>
          </button>
        </div>
      </div>
    </div>
    <ng-container [ngSwitch]="apiVersion">
      <app-segment-list-table *ngSwitchCase="apiVersionHandle.V1"
                              (selectedSegmentIds)="onSelect($event)"
                              [config]="config"
                              [columnConfig]="columnsConfig$ | async"
                              [searchFormValues]="searchFormValues"></app-segment-list-table>
      <app-tree-table *ngSwitchCase="apiVersionHandle.V2"
                      (selectedSegmentIds)="onSelect($event)"
                      [config]="config"
                      [columnConfig]="hierarchicalColumnsConfig$ | async"
                      [searchFormValues]="searchFormValues"></app-tree-table>
    </ng-container>

  `,
  styleUrls: ['list.component.scss'],
})
export class SegmentListComponent {
  @ViewChild(SegmentListTableComponent) segmentListTable?: SegmentListTableComponent;
  @ViewChild(TreeTableComponent) treeTable?: TreeTableComponent;
  @Input() config!: SegmentListConfig;
  @Input() apiVersion: ApiVersion = ApiVersion.V1;
  apiVersionHandle: typeof ApiVersion = ApiVersion;
  @Output() showCreateSegmentDialog = new EventEmitter<void>();
  searchFormValues: SegmentSearchFilterModel = {};
  segmentSelection: number[] = [];
  partnerId$: Observable<number> = this.store.select(selectPartnerId);
  columnsConfig$ = this.customConfigurationFacade.selectTableColumns();
  hierarchicalColumnsConfig$ = this.customConfigurationFacade.selectHierarchicalTableColumns();

  constructor(private store: Store,
              private segmentShareService: SegmentShareService,
              private exportPreparer: ExportPreparerService,
              private segmentFacade: SegmentFacadeService,
              private dialog: MatDialog,
              private customConfigurationFacade: CustomConfigurationFacadeService,
              private router: Router) {
  }

  create() {
    this.showCreateSegmentDialog.emit();
  }

  onSelect(selectedSegments: number[]) {
    this.segmentSelection = selectedSegments;
    this.segmentFacade.selectionChanged(selectedSegments);
  }

  clearSelection() {
    this.segmentSelection = [];
    this.segmentListTable?.clearSelection();
    this.treeTable?.clearSelection();
  }

  handleSearchForm(formValue: SegmentSearchFilterModel) {
    this.searchFormValues = formValue;
  }

  handleExport() {
    this.exportPreparer.prepareExportPreview({
        ...this.searchFormValues,
        withPlatformAttributes: true,
        segmentIds: this.segmentSelection.length >= 1 ?
          [...this.segmentSelection.map(it => `"${it.toString()}"`)] : this.searchFormValues.segmentIds
      },
      this.apiVersion
    )
    ;
  }

  showCreateSegment(partnerId: number): boolean {
    return partnerId !== UserModel.ALL_PARTNER_ID;
  }

  multiShare() {
    this.segmentShareService.reset();
    this.segmentSelection.map(segmentId => this.segmentShareService.addSegmentToSelection(segmentId));
    this.router.navigate(['segmentsmultishare']).then(() => {
    });
  }

  handleExportPrices() {
    this.dialog.open(ExportPricesDialogComponent);
  }

  bulkDelete() {
    this.dialog.open(BulkDeleteSegmentsDialogComponent, {maxWidth: 500, data: <BulkDeleteSegmentsData>{segmentIds: this.segmentSelection}});
  }
}
